import React, { useState, ReactElement } from 'react';
import styled from '@emotion/styled';
import { useSelector } from 'react-redux';
import { idvPurchaseEnabled } from '../config/Master';
import { RootState } from '../store';
import SVButton from '../atoms/buttons/SVButton';
import ConfirmSelfieVerificationModal from './modals/verification/ConfirmSelfieVerificationModal';
import { isDarkMode } from 'utils/helpers';

const InfoItemDescription = styled('div')(() => ({
  fontSize: 'inherit',
  width: '100%',
}));

const getSideBarStyle = () => ({
  margin: '0 auto',
  minWidth: 'unset',
  fontSize: 15,
  borderRadius: '4px !important',
  fontStyle: 'italic',
  padding: '23px 10px',
});

interface SelfieVerificationProps {
  sideBar?: boolean;
  customIDVerificationStatusStyle?: React.CSSProperties;
  isProfile?: boolean;
}

const SelfieVerification = (props: SelfieVerificationProps): ReactElement => {
  const { sideBar = false } = props;

  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const {
    liveness,
    profile: {
      data: { selfie_verified },
    },
  } = useSelector((state: RootState) => state.profile);

  const renderBtnIDV = () => {
    const isNotStarted = ['not_started', 'required', 'not_required'].includes(liveness?.status);
    const isNotComplete = ['skipped', 'error'].includes(liveness?.status);
    const isFailed = !selfie_verified && liveness?.status === 'denied';
    const isPending = !['skipped', 'error', 'approved', 'approved_by_idv'].includes(liveness?.status);
    const isCompleted = ['approved', 'approved_by_idv'].includes(liveness?.status);

    const customSideStyle = sideBar ? getSideBarStyle() : {};

    if (isFailed) {
      return (
        <SVButton
          customStyle={{
            ...customSideStyle,
          }}
          status={'unsuccessful'}
          buttonContent={
            <>
              <InfoItemDescription
                style={{
                  fontSize: 12,
                  fontWeight: 600,
                  color: isDarkMode() ? '#F08EA4' : '#DD0B24',
                  fontStyle: 'italic',
                }}
              >
                Verification Failed
              </InfoItemDescription>
            </>
          }
        />
      );
    }

    if (isNotStarted) {
      return (
        <SVButton
          data-test-id={sideBar ? 'sidebar-buy-idv' : 'btn-buy-idv'}
          customStyle={{
            ...customSideStyle,
            fontStyle: 'normal',
          }}
          onClick={() => {
            document.body.style.overflow = 'hidden';
            setShowConfirmModal(true);
          }}
          status={'getIdVerified'}
          buttonContent={
            <>
              <span style={{ fontWeight: 400, color: 'inherit' }}>Get&nbsp;</span>Selfie Verified
            </>
          }
        />
      );
    }

    if (isNotComplete) {
      return (
        <SVButton
          customStyle={{
            ...customSideStyle,
          }}
          onClick={() => {
            document.body.style.overflow = 'hidden';
            setShowConfirmModal(true);
          }}
          status={'idvNotComplete'}
          buttonContent={
            <>
              <InfoItemDescription style={{ fontSize: 14, fontStyle: 'italic', fontWeight: 400 }}>
                SV: <span style={{ fontWeight: 600, color: 'inherit' }}>Not Complete</span>
                <br />
                <span style={{ fontSize: 8, fontWeight: 400, textDecoration: 'underline', color: 'inherit' }}>
                  (Tap to try again)
                </span>
              </InfoItemDescription>
            </>
          }
        />
      );
    }

    if (isPending) {
      return (
        <SVButton
          customStyle={{
            ...customSideStyle,
          }}
          status={'pending'}
          buttonContent={
            <>
              <InfoItemDescription style={{ fontStyle: 'italic', fontWeight: '400', lineHeight: 'normal' }}>
                SV Pending...
              </InfoItemDescription>
            </>
          }
        />
      );
    }

    if (isCompleted) {
      return (
        <SVButton
          customStyle={{
            ...customSideStyle,
          }}
          status="completed"
          buttonContent={
            <>
              <div style={{ fontWeight: 600, fontStyle: 'italic' }}>Selfie Verified!</div>
            </>
          }
        />
      );
    }

    return <></>;
  };

  return (
    <React.Fragment>
      {showConfirmModal && (
        <ConfirmSelfieVerificationModal
          onClose={() => {
            document.body.style.overflow = 'unset';
            setShowConfirmModal(false);
          }}
          contentLabel={'Confirm Selfie Verification'}
          isOpen={showConfirmModal}
          onConfirm={() => {
            setShowConfirmModal(false);
          }}
        />
      )}
      {idvPurchaseEnabled && renderBtnIDV()}
    </React.Fragment>
  );
};

export default SelfieVerification;
